import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  Building2, Palette, Settings, MessageSquare, LogOut, Plus, Pencil, Trash2, 
  Eye, LayoutDashboard, Mail, Phone, MapPin, Image, Check, Calendar
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth helper
const getAuthHeader = (username, password) => {
  return {
    headers: {
      'Authorization': 'Basic ' + btoa(`${username}:${password}`)
    }
  };
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, {}, getAuthHeader(username, password));
      onLogin(username, password);
      toast.success("Login successful");
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-container" data-testid="admin-login">
      <Card className="admin-login-card">
        <CardHeader>
          <CardTitle className="admin-login-title">
            <Settings size={32} />
            Admin Panel
          </CardTitle>
          <p className="admin-login-subtitle">Montgomery's Auction House</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="admin-login-form">
            <div className="form-group">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                data-testid="admin-username"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                data-testid="admin-password"
              />
            </div>
            <Button type="submit" className="admin-login-btn" disabled={loading} data-testid="admin-login-btn">
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ auth }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, getAuthHeader(auth.username, auth.password));
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, [auth]);

  if (!stats) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <h2 className="admin-section-title">Dashboard</h2>
      <div className="stats-grid">
        <Card className="stat-card">
          <CardContent className="stat-content">
            <Building2 size={32} className="stat-icon" />
            <div className="stat-info">
              <span className="stat-value">{stats.real_estate_count}</span>
              <span className="stat-label">Properties</span>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="stat-content">
            <Palette size={32} className="stat-icon" />
            <div className="stat-info">
              <span className="stat-value">{stats.art_count}</span>
              <span className="stat-label">Artworks</span>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="stat-content">
            <Calendar size={32} className="stat-icon" />
            <div className="stat-info">
              <span className="stat-value">{stats.events_count ?? 0}</span>
              <span className="stat-label">Events</span>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="stat-content">
            <MessageSquare size={32} className="stat-icon" />
            <div className="stat-info">
              <span className="stat-value">{stats.inquiries_count}</span>
              <span className="stat-label">Inquiries</span>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card highlight">
          <CardContent className="stat-content">
            <Mail size={32} className="stat-icon" />
            <div className="stat-info">
              <span className="stat-value">{stats.unread_inquiries}</span>
              <span className="stat-label">Unread</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Real Estate Management
const RealEstateManagement = ({ auth }) => {
  const [listings, setListings] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "", location: "", description: "", price: "", bedrooms: "", bathrooms: "",
    sqft: "", property_type: "mansion", image_url: "", featured: false, auction_end_date: ""
  });

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/admin/real-estate`, getAuthHeader(auth.username, auth.password));
      setListings(response.data);
    } catch (error) {
      toast.error("Failed to fetch listings");
    }
  };

  useEffect(() => { fetchListings(); }, [auth]);

  const resetForm = () => {
    setFormData({
      title: "", location: "", description: "", price: "", bedrooms: "", bathrooms: "",
      sqft: "", property_type: "mansion", image_url: "", featured: false, auction_end_date: ""
    });
    setEditingItem(null);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      location: item.location,
      description: item.description,
      price: item.price.toString(),
      bedrooms: item.bedrooms.toString(),
      bathrooms: item.bathrooms.toString(),
      sqft: item.sqft.toString(),
      property_type: item.property_type,
      image_url: item.image_url,
      featured: item.featured,
      auction_end_date: item.auction_end_date || ""
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      bedrooms: parseInt(formData.bedrooms),
      bathrooms: parseInt(formData.bathrooms),
      sqft: parseInt(formData.sqft),
      auction_end_date: formData.auction_end_date || null
    };

    try {
      if (editingItem) {
        await axios.put(`${API}/admin/real-estate/${editingItem.id}`, payload, getAuthHeader(auth.username, auth.password));
        toast.success("Property updated");
      } else {
        await axios.post(`${API}/admin/real-estate`, payload, getAuthHeader(auth.username, auth.password));
        toast.success("Property created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchListings();
    } catch (error) {
      toast.error("Failed to save property");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;
    try {
      await axios.delete(`${API}/admin/real-estate/${id}`, getAuthHeader(auth.username, auth.password));
      toast.success("Property deleted");
      fetchListings();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  return (
    <div className="admin-management" data-testid="admin-real-estate">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Real Estate</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="admin-add-btn" data-testid="add-property-btn">
              <Plus size={18} /> Add Property
            </Button>
          </DialogTrigger>
          <DialogContent className="admin-dialog">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Edit Property" : "Add Property"}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="admin-dialog-scroll">
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-group">
                  <Label>Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
                </div>
                <div className="form-group">
                  <Label>Location</Label>
                  <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required />
                </div>
                <div className="form-group">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={4} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <Label>Price ($)</Label>
                    <Input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <Label>Property Type</Label>
                    <Select value={formData.property_type} onValueChange={(v) => setFormData({...formData, property_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mansion">Mansion</SelectItem>
                        <SelectItem value="penthouse">Penthouse</SelectItem>
                        <SelectItem value="villa">Villa</SelectItem>
                        <SelectItem value="estate">Estate</SelectItem>
                        <SelectItem value="townhouse">Townhouse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="form-row three">
                  <div className="form-group">
                    <Label>Bedrooms</Label>
                    <Input type="number" value={formData.bedrooms} onChange={(e) => setFormData({...formData, bedrooms: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <Label>Bathrooms</Label>
                    <Input type="number" value={formData.bathrooms} onChange={(e) => setFormData({...formData, bathrooms: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <Label>Sqft</Label>
                    <Input type="number" value={formData.sqft} onChange={(e) => setFormData({...formData, sqft: e.target.value})} required />
                  </div>
                </div>
                <div className="form-group">
                  <Label>Image URL</Label>
                  <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} placeholder="https://..." required />
                </div>
                <div className="form-group">
                  <Label>Auction End Date</Label>
                  <Input type="datetime-local" value={formData.auction_end_date ? formData.auction_end_date.slice(0, 16) : ""} onChange={(e) => setFormData({...formData, auction_end_date: e.target.value ? new Date(e.target.value).toISOString() : ""})} />
                </div>
                <div className="form-group-inline">
                  <Switch checked={formData.featured} onCheckedChange={(v) => setFormData({...formData, featured: v})} />
                  <Label>Featured Listing</Label>
                </div>
                <Button type="submit" className="admin-submit-btn">{editingItem ? "Update" : "Create"} Property</Button>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      <div className="admin-list">
        {listings.map((item) => (
          <Card key={item.id} className="admin-list-item">
            <div className="admin-item-image">
              <img src={item.image_url} alt={item.title} />
            </div>
            <div className="admin-item-content">
              <div className="admin-item-header">
                <h3>{item.title}</h3>
                {item.featured && <Badge className="featured-badge">Featured</Badge>}
              </div>
              <p className="admin-item-location"><MapPin size={14} /> {item.location}</p>
              <p className="admin-item-price">${item.price.toLocaleString()}</p>
              <p className="admin-item-details">{item.bedrooms} beds • {item.bathrooms} baths • {item.sqft.toLocaleString()} sqft</p>
            </div>
            <div className="admin-item-actions">
              <Button variant="outline" size="sm" onClick={() => openEditDialog(item)}><Pencil size={16} /></Button>
              <Button variant="outline" size="sm" className="delete-btn" onClick={() => handleDelete(item.id)}><Trash2 size={16} /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Art Management
const ArtManagement = ({ auth }) => {
  const [listings, setListings] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "", artist: "", year: "", description: "", estimated_value_low: "", estimated_value_high: "",
    current_bid: "", category: "painting", medium: "", dimensions: "", image_url: "", featured: false, auction_end_date: ""
  });

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/admin/art`, getAuthHeader(auth.username, auth.password));
      setListings(response.data);
    } catch (error) {
      toast.error("Failed to fetch artworks");
    }
  };

  useEffect(() => { fetchListings(); }, [auth]);

  const resetForm = () => {
    setFormData({
      title: "", artist: "", year: "", description: "", estimated_value_low: "", estimated_value_high: "",
      current_bid: "", category: "painting", medium: "", dimensions: "", image_url: "", featured: false, auction_end_date: ""
    });
    setEditingItem(null);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      artist: item.artist,
      year: item.year?.toString() || "",
      description: item.description,
      estimated_value_low: item.estimated_value_low.toString(),
      estimated_value_high: item.estimated_value_high.toString(),
      current_bid: item.current_bid?.toString() || "",
      category: item.category,
      medium: item.medium,
      dimensions: item.dimensions || "",
      image_url: item.image_url,
      featured: item.featured,
      auction_end_date: item.auction_end_date || ""
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      year: formData.year ? parseInt(formData.year) : null,
      estimated_value_low: parseFloat(formData.estimated_value_low),
      estimated_value_high: parseFloat(formData.estimated_value_high),
      current_bid: formData.current_bid ? parseFloat(formData.current_bid) : null,
      auction_end_date: formData.auction_end_date || null
    };

    try {
      if (editingItem) {
        await axios.put(`${API}/admin/art/${editingItem.id}`, payload, getAuthHeader(auth.username, auth.password));
        toast.success("Artwork updated");
      } else {
        await axios.post(`${API}/admin/art`, payload, getAuthHeader(auth.username, auth.password));
        toast.success("Artwork created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchListings();
    } catch (error) {
      toast.error("Failed to save artwork");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this artwork?")) return;
    try {
      await axios.delete(`${API}/admin/art/${id}`, getAuthHeader(auth.username, auth.password));
      toast.success("Artwork deleted");
      fetchListings();
    } catch (error) {
      toast.error("Failed to delete artwork");
    }
  };

  return (
    <div className="admin-management" data-testid="admin-art">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Fine Art</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="admin-add-btn" data-testid="add-art-btn">
              <Plus size={18} /> Add Artwork
            </Button>
          </DialogTrigger>
          <DialogContent className="admin-dialog">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Edit Artwork" : "Add Artwork"}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="admin-dialog-scroll">
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-group">
                  <Label>Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <Label>Artist</Label>
                    <Input value={formData.artist} onChange={(e) => setFormData({...formData, artist: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <Label>Year</Label>
                    <Input type="number" value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} />
                  </div>
                </div>
                <div className="form-group">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={4} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="painting">Painting</SelectItem>
                        <SelectItem value="sculpture">Sculpture</SelectItem>
                        <SelectItem value="contemporary">Contemporary</SelectItem>
                        <SelectItem value="mixed_media">Mixed Media</SelectItem>
                        <SelectItem value="photography">Photography</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-group">
                    <Label>Medium</Label>
                    <Input value={formData.medium} onChange={(e) => setFormData({...formData, medium: e.target.value})} placeholder="Oil on canvas" required />
                  </div>
                </div>
                <div className="form-group">
                  <Label>Dimensions</Label>
                  <Input value={formData.dimensions} onChange={(e) => setFormData({...formData, dimensions: e.target.value})} placeholder="120 × 180 cm" />
                </div>
                <div className="form-row three">
                  <div className="form-group">
                    <Label>Est. Low ($)</Label>
                    <Input type="number" value={formData.estimated_value_low} onChange={(e) => setFormData({...formData, estimated_value_low: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <Label>Est. High ($)</Label>
                    <Input type="number" value={formData.estimated_value_high} onChange={(e) => setFormData({...formData, estimated_value_high: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <Label>Current Bid ($)</Label>
                    <Input type="number" value={formData.current_bid} onChange={(e) => setFormData({...formData, current_bid: e.target.value})} />
                  </div>
                </div>
                <div className="form-group">
                  <Label>Image URL</Label>
                  <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} placeholder="https://..." required />
                </div>
                <div className="form-group">
                  <Label>Auction End Date</Label>
                  <Input type="datetime-local" value={formData.auction_end_date ? formData.auction_end_date.slice(0, 16) : ""} onChange={(e) => setFormData({...formData, auction_end_date: e.target.value ? new Date(e.target.value).toISOString() : ""})} />
                </div>
                <div className="form-group-inline">
                  <Switch checked={formData.featured} onCheckedChange={(v) => setFormData({...formData, featured: v})} />
                  <Label>Featured Listing</Label>
                </div>
                <Button type="submit" className="admin-submit-btn">{editingItem ? "Update" : "Create"} Artwork</Button>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      <div className="admin-list">
        {listings.map((item) => (
          <Card key={item.id} className="admin-list-item">
            <div className="admin-item-image art">
              <img src={item.image_url} alt={item.title} />
            </div>
            <div className="admin-item-content">
              <div className="admin-item-header">
                <h3>{item.title}</h3>
                {item.featured && <Badge className="featured-badge">Featured</Badge>}
              </div>
              <p className="admin-item-artist">{item.artist}{item.year && `, ${item.year}`}</p>
              <p className="admin-item-price">${item.estimated_value_low.toLocaleString()} - ${item.estimated_value_high.toLocaleString()}</p>
              <p className="admin-item-details">{item.category} • {item.medium}</p>
            </div>
            <div className="admin-item-actions">
              <Button variant="outline" size="sm" onClick={() => openEditDialog(item)}><Pencil size={16} /></Button>
              <Button variant="outline" size="sm" className="delete-btn" onClick={() => handleDelete(item.id)}><Trash2 size={16} /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Events Management
const EventsManagement = ({ auth }) => {
  const [listings, setListings] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "", event_date: "", location: "", description: "", image_url: "", featured: false
  });

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/admin/events`, getAuthHeader(auth.username, auth.password));
      setListings(response.data);
    } catch (error) {
      toast.error("Failed to fetch events");
    }
  };

  useEffect(() => { fetchListings(); }, [auth]);

  const resetForm = () => {
    setFormData({ title: "", event_date: "", location: "", description: "", image_url: "", featured: false });
    setEditingItem(null);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      event_date: item.event_date || "",
      location: item.location,
      description: item.description,
      image_url: item.image_url,
      featured: item.featured
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, event_date: formData.event_date || null };
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/events/${editingItem.id}`, payload, getAuthHeader(auth.username, auth.password));
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/admin/events`, payload, getAuthHeader(auth.username, auth.password));
        toast.success("Event created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchListings();
    } catch (error) {
      toast.error("Failed to save event");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/events/${id}`, getAuthHeader(auth.username, auth.password));
      toast.success("Event deleted");
      fetchListings();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  return (
    <div className="admin-management" data-testid="admin-events">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Events</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="admin-add-btn" data-testid="add-event-btn">
              <Plus size={18} /> Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="admin-dialog">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Edit Event" : "Add Event"}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="admin-dialog-scroll">
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-group">
                  <Label>Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="event-title-input" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <Label>Date & Time</Label>
                    <Input type="datetime-local" value={formData.event_date ? formData.event_date.slice(0, 16) : ""} onChange={(e) => setFormData({...formData, event_date: e.target.value ? new Date(e.target.value).toISOString() : ""})} />
                  </div>
                  <div className="form-group">
                    <Label>Location / Venue</Label>
                    <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required />
                  </div>
                </div>
                <div className="form-group">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={5} required />
                </div>
                <div className="form-group">
                  <Label>Image URL</Label>
                  <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} placeholder="https://..." required />
                </div>
                <div className="form-group-inline">
                  <Switch checked={formData.featured} onCheckedChange={(v) => setFormData({...formData, featured: v})} />
                  <Label>Featured Event</Label>
                </div>
                <Button type="submit" className="admin-submit-btn" data-testid="event-submit-btn">{editingItem ? "Update" : "Create"} Event</Button>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      <div className="admin-list">
        {listings.length === 0 ? (
          <p className="no-inquiries">No events yet</p>
        ) : listings.map((item) => (
          <Card key={item.id} className="admin-list-item">
            <div className="admin-item-image">
              <img src={item.image_url} alt={item.title} />
            </div>
            <div className="admin-item-content">
              <div className="admin-item-header">
                <h3>{item.title}</h3>
                {item.featured && <Badge className="featured-badge">Featured</Badge>}
              </div>
              {item.event_date && <p className="admin-item-location"><Calendar size={14} /> {new Date(item.event_date).toLocaleString()}</p>}
              <p className="admin-item-location"><MapPin size={14} /> {item.location}</p>
              <p className="admin-item-details">{item.description.slice(0, 120)}{item.description.length > 120 ? "…" : ""}</p>
            </div>
            <div className="admin-item-actions">
              <Button variant="outline" size="sm" onClick={() => openEditDialog(item)}><Pencil size={16} /></Button>
              <Button variant="outline" size="sm" className="delete-btn" onClick={() => handleDelete(item.id)}><Trash2 size={16} /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Site Settings Management
const SiteSettingsManagement = ({ auth }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, getAuthHeader(auth.username, auth.password));
      setSettings(response.data);
    } catch (error) {
      toast.error("Failed to fetch settings");
    }
  };

  useEffect(() => { fetchSettings(); }, [auth]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, getAuthHeader(auth.username, auth.password));
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  if (!settings) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-settings" data-testid="admin-settings">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Site Settings</h2>
        <Button className="admin-save-btn" onClick={handleSave} disabled={loading}>
          <Check size={18} /> {loading ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="settings-sections">
        <Card className="settings-card">
          <CardHeader>
            <CardTitle>Company Information</CardTitle>
          </CardHeader>
          <CardContent className="settings-form">
            <div className="form-group">
              <Label>Company Name</Label>
              <Input value={settings.company_name} onChange={(e) => setSettings({...settings, company_name: e.target.value})} />
            </div>
            <div className="form-group">
              <Label>Tagline (Hero Subtitle)</Label>
              <Input value={settings.tagline} onChange={(e) => setSettings({...settings, tagline: e.target.value})} />
            </div>
            <div className="form-group">
              <Label>Description (Hero Text)</Label>
              <Textarea value={settings.description} onChange={(e) => setSettings({...settings, description: e.target.value})} rows={3} />
            </div>
            <div className="form-group">
              <Label>Footer Tagline</Label>
              <Input value={settings.footer_tagline} onChange={(e) => setSettings({...settings, footer_tagline: e.target.value})} />
            </div>
          </CardContent>
        </Card>

        <Card className="settings-card">
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="settings-form">
            <div className="form-row">
              <div className="form-group">
                <Label><Phone size={14} /> Phone</Label>
                <Input value={settings.phone} onChange={(e) => setSettings({...settings, phone: e.target.value})} />
              </div>
              <div className="form-group">
                <Label><Mail size={14} /> Email</Label>
                <Input value={settings.email} onChange={(e) => setSettings({...settings, email: e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <Label><MapPin size={14} /> Address</Label>
              <Input value={settings.address} onChange={(e) => setSettings({...settings, address: e.target.value})} />
            </div>
            <div className="form-group">
              <Label>City / Postal</Label>
              <Input value={settings.city} onChange={(e) => setSettings({...settings, city: e.target.value})} />
            </div>
          </CardContent>
        </Card>

        <Card className="settings-card">
          <CardHeader>
            <CardTitle>About Page Content</CardTitle>
          </CardHeader>
          <CardContent className="settings-form">
            <div className="form-group">
              <Label>Heritage Section</Label>
              <Textarea value={settings.about_heritage} onChange={(e) => setSettings({...settings, about_heritage: e.target.value})} rows={5} />
            </div>
            <div className="form-group">
              <Label>Commitment Section</Label>
              <Textarea value={settings.about_commitment} onChange={(e) => setSettings({...settings, about_commitment: e.target.value})} rows={5} />
            </div>
          </CardContent>
        </Card>

        <Card className="settings-card">
          <CardHeader>
            <CardTitle>Hero Image</CardTitle>
          </CardHeader>
          <CardContent className="settings-form">
            <div className="form-group">
              <Label><Image size={14} /> Hero Background URL</Label>
              <Input value={settings.hero_image_url} onChange={(e) => setSettings({...settings, hero_image_url: e.target.value})} placeholder="https://..." />
            </div>
            {settings.hero_image_url && (
              <div className="image-preview">
                <img src={settings.hero_image_url} alt="Hero preview" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Inquiries Management
const InquiriesManagement = ({ auth }) => {
  const [inquiries, setInquiries] = useState([]);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/admin/inquiries`, getAuthHeader(auth.username, auth.password));
      setInquiries(response.data);
    } catch (error) {
      toast.error("Failed to fetch inquiries");
    }
  };

  useEffect(() => { fetchInquiries(); }, [auth]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/read`, {}, getAuthHeader(auth.username, auth.password));
      fetchInquiries();
    } catch (error) {
      toast.error("Failed to update inquiry");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, getAuthHeader(auth.username, auth.password));
      toast.success("Inquiry deleted");
      fetchInquiries();
    } catch (error) {
      toast.error("Failed to delete inquiry");
    }
  };

  return (
    <div className="admin-inquiries" data-testid="admin-inquiries">
      <h2 className="admin-section-title">Inquiries</h2>
      <div className="inquiries-list">
        {inquiries.length === 0 ? (
          <p className="no-inquiries">No inquiries yet</p>
        ) : (
          inquiries.map((inquiry) => (
            <Card key={inquiry.id} className={`inquiry-card ${!inquiry.read ? 'unread' : ''}`}>
              <CardContent className="inquiry-content">
                <div className="inquiry-header">
                  <div className="inquiry-info">
                    <h4>{inquiry.name}</h4>
                    <p className="inquiry-email">{inquiry.email}</p>
                    {inquiry.phone && <p className="inquiry-phone">{inquiry.phone}</p>}
                  </div>
                  <div className="inquiry-meta">
                    {!inquiry.read && <Badge className="unread-badge">New</Badge>}
                    {inquiry.listing_type && (
                      <Badge variant="outline">{inquiry.listing_type}</Badge>
                    )}
                    <span className="inquiry-date">
                      {new Date(inquiry.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <p className="inquiry-message">{inquiry.message}</p>
                <div className="inquiry-actions">
                  {!inquiry.read && (
                    <Button variant="outline" size="sm" onClick={() => markAsRead(inquiry.id)}>
                      <Eye size={14} /> Mark Read
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="delete-btn" onClick={() => handleDelete(inquiry.id)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Main Admin Panel
const AdminPanel = () => {
  const [auth, setAuth] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");

  const handleLogin = (username, password) => {
    setAuth({ username, password });
  };

  const handleLogout = () => {
    setAuth(null);
  };

  if (!auth) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="admin-panel" data-testid="admin-panel">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h1>Montgomery's</h1>
          <p>Admin Panel</p>
        </div>
        <nav className="admin-nav">
          <button className={`admin-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
            <LayoutDashboard size={20} /> Dashboard
          </button>
          <button className={`admin-nav-item ${activeTab === 'real-estate' ? 'active' : ''}`} onClick={() => setActiveTab('real-estate')}>
            <Building2 size={20} /> Real Estate
          </button>
          <button className={`admin-nav-item ${activeTab === 'art' ? 'active' : ''}`} onClick={() => setActiveTab('art')}>
            <Palette size={20} /> Fine Art
          </button>
          <button className={`admin-nav-item ${activeTab === 'events' ? 'active' : ''}`} onClick={() => setActiveTab('events')} data-testid="admin-nav-events">
            <Calendar size={20} /> Events
          </button>
          <button className={`admin-nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
            <Settings size={20} /> Site Settings
          </button>
          <button className={`admin-nav-item ${activeTab === 'inquiries' ? 'active' : ''}`} onClick={() => setActiveTab('inquiries')}>
            <MessageSquare size={20} /> Inquiries
          </button>
        </nav>
        <div className="admin-sidebar-footer">
          <Button variant="outline" className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} /> Logout
          </Button>
        </div>
      </aside>
      <main className="admin-main">
        {activeTab === 'dashboard' && <Dashboard auth={auth} />}
        {activeTab === 'real-estate' && <RealEstateManagement auth={auth} />}
        {activeTab === 'art' && <ArtManagement auth={auth} />}
        {activeTab === 'events' && <EventsManagement auth={auth} />}
        {activeTab === 'settings' && <SiteSettingsManagement auth={auth} />}
        {activeTab === 'inquiries' && <InquiriesManagement auth={auth} />}
      </main>
    </div>
  );
};

export default AdminPanel;
